import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api/axios';
import notificationSound from '../utils/notificationSound';
import getImageUrl from '../utils/imageUrl';

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const previousUnreadCount = useRef(0);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      const notificationsData = res.data?.data || res.data || [];
      const adminNotifications = notificationsData.filter(n => n.user_id === null);
      // Sort by created_at descending (newest first)
      const sortedNotifications = adminNotifications.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      const recentNotifications = sortedNotifications.slice(0, 10);
      const newUnreadCount = recentNotifications.filter(n => !n.is_read).length;
      
      // Only play sound if dropdown is being opened AND there are unread notifications
      // Don't play on initial load or refresh
      if (isOpen && newUnreadCount > 0 && previousUnreadCount.current !== null && newUnreadCount > previousUnreadCount.current) {
        notificationSound.playBookingNotification();
      }
      
      // Animate bell only if there are NEW notifications (not on refresh)
      if (previousUnreadCount.current !== null && newUnreadCount > previousUnreadCount.current) {
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 1000);
      }
      
      setNotifications(recentNotifications);
      setUnreadCount(newUnreadCount);
      previousUnreadCount.current = newUnreadCount;
    } catch (error) {
      if (error.code !== 'ECONNABORTED' && error.message !== 'Network Error') {
        console.error('Error fetching notifications:', error.message);
      }
    }
  };

  useEffect(() => {
    // Set initial value to null to prevent sound on first load
    previousUnreadCount.current = null;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, []);

  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}`, { is_read: true });
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
      await Promise.all(unreadIds.map(id => 
        api.put(`/notifications/${id}`, { is_read: true })
      ));
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      fetchNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      info: '📢',
      success: '✅',
      warning: '⚠️',
      error: '❌',
      booking: '🏠',
      payment: '💳'
    };
    return icons[type] || '📢';
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (diff < 0) return 'Just now';
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  };

  return (
    <div className="relative">
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-blue-600 transition-colors"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        animate={isAnimating ? { 
          rotate: [0, -10, 10, -10, 10, 0],
          scale: [1, 1.1, 1, 1.1, 1]
        } : {}}
        transition={{ duration: 0.5 }}
      >
        <motion.svg 
          className="w-6 h-6" 
          fill="currentColor" 
          viewBox="0 0 20 20"
          animate={unreadCount > 0 ? {
            rotate: [0, 15, -15, 0]
          } : {}}
          transition={{ 
            duration: 0.5,
            repeat: unreadCount > 0 ? Infinity : 0,
            repeatDelay: 2
          }}
        >
          <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
        </motion.svg>
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span 
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute right-0 mt-2 w-80 sm:w-96 max-w-[90vw] bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 z-50 overflow-hidden"
            >
              {/* Header with blue gradient */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <motion.div
                      animate={{ rotate: [0, 15, -15, 0] }}
                      transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
                      className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center"
                    >
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                      </svg>
                    </motion.div>
                    <div>
                      <h3 className="text-lg font-bold text-white">Notifications</h3>
                      {unreadCount > 0 && (
                        <motion.span 
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="text-xs text-white/80"
                        >
                          {unreadCount} unread message{unreadCount > 1 ? 's' : ''}
                        </motion.span>
                      )}
                    </div>
                  </div>
                  {unreadCount > 0 && (
                    <motion.button
                      onClick={markAllAsRead}
                      className="bg-white/20 hover:bg-white/30 text-white text-xs px-3 py-1.5 rounded-full transition-all duration-200"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Mark all read
                    </motion.button>
                  )}
                </div>
              </div>

              {/* Notifications List */}
              <div className="max-h-96 overflow-y-auto">
                {notifications.length === 0 ? (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-8 text-center"
                  >
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-2xl">🔔</span>
                    </div>
                    <p className="text-gray-500 font-medium">No notifications yet</p>
                    <p className="text-gray-400 text-sm mt-1">You'll see booking alerts here</p>
                  </motion.div>
                ) : (
                  notifications.map((notification, index) => (
                    <motion.div
                      key={notification.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`relative p-4 hover:bg-gray-50/80 transition-all duration-200 group ${
                        !notification.is_read 
                          ? 'bg-blue-50/50 border-l-4 border-l-blue-500' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* Notification Item */}
                      <div className="flex items-start space-x-3">
                        {/* Property Image or Icon */}
                        <motion.div 
                          className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden bg-gradient-to-br from-blue-400 to-blue-600"
                          animate={!notification.is_read ? {
                            scale: [1, 1.05, 1],
                            rotate: [0, 2, -2, 0]
                          } : {}}
                          transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                        >
                          {notification.data?.property_image ? (
                            <img 
                              src={getImageUrl(notification.data.property_image)} 
                              alt={notification.data.property_name || 'Property'}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className={`w-full h-full flex items-center justify-center ${
                              notification.data?.property_image ? 'hidden' : 'flex'
                            }`}
                          >
                            <svg 
                              className="w-4 h-4 sm:w-6 sm:h-6 text-white" 
                              fill="currentColor" 
                              viewBox="0 0 20 20"
                            >
                              <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                            </svg>
                          </div>
                        </motion.div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className={`font-semibold text-xs sm:text-sm ${
                                !notification.is_read ? 'text-gray-900' : 'text-gray-700'
                              } line-clamp-2`}>
                                {notification.title}
                              </p>
                              {notification.data ? (
                                <div className="text-xs sm:text-sm text-gray-600 mt-1 space-y-1">
                                  <p className="text-xs truncate">
                                    <i className="far fa-calendar"></i> {notification.data.start_date || 'N/A'} - {notification.data.end_date || 'N/A'}
                                  </p>
                                  <p className="text-xs">
                                    <i className="fas fa-money-bill-wave"></i> Rp {notification.data.total_amount ? new Intl.NumberFormat('id-ID').format(notification.data.total_amount) : '0'}
                                  </p>
                                  {notification.data.guests && (
                                    <p className="text-xs"><i className="fas fa-users"></i> {notification.data.guests} tamu</p>
                                  )}
                                </div>
                              ) : (
                                <p className="text-xs sm:text-sm text-gray-600 mt-1 leading-relaxed line-clamp-2">
                                  {notification.message}
                                </p>
                              )}
                              
                              {/* Time and status */}
                              <div className="flex items-center justify-between mt-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center text-xs text-gray-400">
                                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-2"></div>
                                    <span className="truncate">{formatTime(notification.created_at)}</span>
                                  </div>
                                  
                                  {/* Action buttons */}
                                  <div className="flex items-center space-x-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                    {!notification.is_read && (
                                      <motion.button
                                        onClick={() => markAsRead(notification.id)}
                                        className="bg-blue-100 hover:bg-blue-200 text-blue-600 text-xs px-1.5 sm:px-2 py-1 rounded-md transition-colors"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        title="Mark as read"
                                      >
                                        <span className="hidden sm:inline">✓ Read</span>
                                        <span className="sm:hidden">✓</span>
                                      </motion.button>
                                    )}
                                    <motion.button
                                      onClick={() => deleteNotification(notification.id)}
                                      className="bg-red-100 hover:bg-red-200 text-red-600 text-xs px-1.5 sm:px-2 py-1 rounded-md transition-colors"
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      title="Delete"
                                    >
                                      ✕
                                    </motion.button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Unread indicator */}
                      {!notification.is_read && (
                        <motion.div 
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute top-4 right-4 w-2 h-2 bg-blue-500 rounded-full"
                        />
                      )}
                    </motion.div>
                  ))
                )}
              </div>

              {/* Footer */}
              {notifications.length > 0 && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-4 bg-gray-50/50 border-t border-gray-100"
                >
                  <motion.a
                    href="/admin/notifications"
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm font-medium py-2.5 px-4 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 no-underline"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>View All Notifications</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </motion.a>
                </motion.div>
              )}
            </motion.div>
            <div
              className="fixed inset-0 z-40 bg-transparent"
              onClick={() => setIsOpen(false)}
            />
          </>
        )}
      </AnimatePresence>
    </div>
  );
}